// serviceFeeInvoice.js
// Builds and downloads the "Tax Invoice" for the Amik CMS/service fee charged to a
// CPO for a payout period. Amik (Uttar Pradesh, GSTIN 09ABACA1808N1ZL) is the
// seller; the CPO team's registered business is the buyer. The Amik CMS Fee
// (payout.amikFee) is GST-inclusive — computed as base * commission% * (1 + tax%) —
// so the taxable value is the fee back-divided by (1 + tax%), and the tax is the
// remainder, split into CGST+SGST for an intra-state buyer (also UP/09) or charged
// as IGST inter-state. Rendered from an off-screen HTML node to an A4 PDF, matching
// the Tally-style layout the finance team already issues.
import jsPDF from "jspdf";

// --- Seller (fixed Amik legal entity) ---------------------------------------
const SELLER = {
  name: "AMIK TECHNOLOGIES PRIVATE LIMITED",
  addressLines: [
    "A9-405, Casa Green 1, Near BLS Public School,",
    "Gautam Buddha Nagar",
  ],
  gstin: "09ABACA1808N1ZL",
  stateName: "Uttar Pradesh",
  stateCode: "09",
};

const SERVICE = { particulars: "Service Charge", hsn: "998599" };

const MON = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// GST state codes (place of supply → CGST/SGST vs IGST). Keyed on lower-cased
// state name; the buyer's GSTIN prefix takes precedence when present.
const STATE_GST_CODES = {
  "jammu and kashmir": "01", "himachal pradesh": "02", punjab: "03",
  chandigarh: "04", uttarakhand: "05", haryana: "06", delhi: "07",
  rajasthan: "08", "uttar pradesh": "09", bihar: "10", sikkim: "11",
  "arunachal pradesh": "12", nagaland: "13", manipur: "14", mizoram: "15",
  tripura: "16", meghalaya: "17", assam: "18", "west bengal": "19",
  jharkhand: "20", odisha: "21", chhattisgarh: "22", "madhya pradesh": "23",
  gujarat: "24", "daman and diu": "25", "dadra and nagar haveli": "26",
  "dadra and nagar haveli and daman and diu": "26", maharashtra: "27",
  "andhra pradesh": "28", karnataka: "29", goa: "30", lakshadweep: "31",
  kerala: "32", "tamil nadu": "33", puducherry: "34",
  "andaman and nicobar islands": "35", telangana: "36", ladakh: "38",
  "other territory": "97",
};
const CODE_TO_STATE = Object.entries(STATE_GST_CODES).reduce((a, [name, code]) => {
  if (!a[code]) a[code] = name.replace(/\b\w/g, (c) => c.toUpperCase());
  return a;
}, {});

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const fmt = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const esc = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
  );
// A displayable value, or "" for anything empty or a literal "undefined"/"null"
// token — so missing address/GSTIN/state parts never render on the invoice.
const clean = (v) => {
  const s = String(v ?? "").trim();
  return !s || /^(undefined|null)$/i.test(s) ? "" : s;
};

// The buyer's two-digit GST state code: the GSTIN prefix if registered, otherwise
// mapped from the state name; "" when neither is known (treated as inter-state).
const deriveStateCode = (billing) => {
  const g = clean(billing?.gstin);
  if (/^\d{2}/.test(g)) return g.slice(0, 2);
  const st = clean(billing?.state).toLowerCase();
  return STATE_GST_CODES[st] || "";
};

// --- Number to Indian-system words ------------------------------------------
const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const twoDigits = (n) =>
  n < 20 ? ONES[n] : (TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "")).trim();
const threeDigits = (n) => {
  const h = Math.floor(n / 100);
  const r = n % 100;
  return [h ? ONES[h] + " Hundred" : "", r ? twoDigits(r) : ""].filter(Boolean).join(" ");
};
const intToIndianWords = (num) => {
  let n = Math.floor(num);
  if (n === 0) return "Zero";
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  return [
    crore ? intToIndianWords(crore) + " Crore" : "",
    lakh ? twoDigits(lakh) + " Lakh" : "",
    thousand ? twoDigits(thousand) + " Thousand" : "",
    n ? threeDigits(n) : "",
  ]
    .filter(Boolean)
    .join(" ");
};
// "INR Nineteen Thousand Three Hundred Fifty Four Only" (paise clause when present).
const amountInWords = (amount) => {
  const rupees = Math.floor(amount + 1e-9);
  const paise = Math.round((amount - rupees) * 100);
  const paiseClause = paise > 0 ? ` and ${twoDigits(paise)} paise` : "";
  return `INR ${intToIndianWords(rupees)}${paiseClause} Only`;
};

// Indian financial year (Apr–Mar) for a month/year, e.g. (3, 2026) → "2025-26".
const financialYear = (year, month) => {
  const start = month >= 4 ? year : year - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
};

// Deterministic invoice number: a pure function of (team, year, month) only — no
// date/time, counter, or random input — so regenerating the same team's invoice for
// the same period always produces the identical number, e.g. "INV/81/03/2025-26".
// Fixed segments: INV / <teamId> / <MM> / <financial year>. It is a display/reference
// number, not an authoritative accounting sequence.
export const buildInvoiceNo = (teamId, year, month) => {
  const team = clean(teamId) || "NA";
  return `INV/${team}/${String(month).padStart(2, "0")}/${financialYear(year, month)}`;
};

// --- Invoice figures --------------------------------------------------------
// All money is derived from the GST-inclusive Amik CMS Fee for the period.
const computeInvoice = (data, { month, year, teamId }) => {
  // Honour an explicit 0% (tax-exempt) period — only fall back to 18 when the rate
  // is genuinely absent/non-numeric, so a 0% fee is not given a fabricated GST split.
  const rawGst = Number(data?.meta?.taxPercent);
  const gstRate = Number.isFinite(rawGst) ? rawGst : 18;
  const feeInclGst = Number(data?.payout?.amikFee) || 0;
  const taxable = round2(feeInclGst / (1 + gstRate / 100));
  const taxAmt = round2((taxable * gstRate) / 100);
  const preRound = taxable + taxAmt;
  const total = Math.round(preRound);
  const roundOff = round2(total - preRound);

  const buyerCode = deriveStateCode(data?.billing);
  const intraState = !!buyerCode && buyerCode === SELLER.stateCode;
  const cgst = intraState ? round2(taxAmt / 2) : 0;
  const sgst = intraState ? round2(taxAmt - cgst) : 0;

  // Deterministic invoice number + financial year (see buildInvoiceNo): the same
  // (team, year, month) always yields the same values on every regeneration.
  const fy = financialYear(year, month);
  const invoiceNo = buildInvoiceNo(teamId, year, month);
  const lastDay = new Date(year, month, 0).getDate();
  const dated = `${String(lastDay).padStart(2, "0")}-${MON[month - 1]}-${String(
    year % 100
  ).padStart(2, "0")}`;

  return {
    gstRate,
    taxable,
    taxAmt,
    roundOff,
    total,
    buyerCode,
    intraState,
    cgst,
    sgst,
    invoiceNo,
    fy,
    dated,
  };
};

// The full record persisted by the "Save to ledger" action AND the exact figures the
// PDF renders — one source of truth so the ledger and the document never disagree.
// opts: { month, year, teamId, invoiceNoOverride? }.
export const buildInvoicePayload = (data, opts) => {
  const inv = computeInvoice(data, opts);
  const invoiceNo = clean(opts?.invoiceNoOverride) || inv.invoiceNo;
  const billing = data?.billing || {};
  const code = deriveStateCode(billing);
  const lastDay = new Date(opts.year, opts.month, 0).getDate();
  const invoiceDate = `${opts.year}-${String(opts.month).padStart(2, "0")}-${String(
    lastDay
  ).padStart(2, "0")}`;
  return {
    teamId: clean(opts?.teamId) || null,
    year: opts.year,
    month: opts.month,
    invoiceNo,
    invoiceDate,
    // This is an Indian GST tax invoice (GSTIN, HSN, CGST/SGST/IGST), which is
    // denominated in INR by statute — the PDF renders ₹ / "INR … Only" to match.
    currency: "INR",
    supplyType: inv.intraState ? "INTRA" : "INTER",
    buyer: {
      name: clean(billing.businessName) || "Customer",
      gstin: clean(billing.gstin),
      state: clean(billing.state) || CODE_TO_STATE[code] || "",
      stateCode: code,
    },
    amounts: {
      gstRate: inv.gstRate,
      taxable: inv.taxable,
      cgst: inv.cgst,
      sgst: inv.sgst,
      igst: inv.intraState ? 0 : inv.taxAmt,
      taxAmount: inv.taxAmt,
      roundOff: inv.roundOff,
      total: inv.total,
    },
  };
};

// --- HTML template ----------------------------------------------------------
const buildBuyerBlock = (billing) => {
  const name = clean(billing?.businessName) || "Customer";
  const a = billing?.address || {};
  const addr = [a.houseNumber, a.street, a.city, a.zipCode, a.country]
    .map(clean)
    .filter(Boolean)
    .join(", ");
  const gstin = clean(billing?.gstin);
  const code = deriveStateCode(billing);
  const stateName = clean(billing?.state) || CODE_TO_STATE[code] || "";
  const stateLine =
    stateName || code
      ? `State Name : ${esc(stateName)}${code ? ", Code : " + esc(code) : ""}`
      : "";
  return `
    <div style="font-weight:bold;">${esc(name)}</div>
    ${addr ? `<div>${esc(addr)}</div>` : ""}
    ${gstin ? `<div>GSTIN/UIN: ${esc(gstin)}</div>` : ""}
    ${stateLine ? `<div>${stateLine}</div>` : ""}`;
};

const buildInvoiceHtml = (data, inv, opts) => {
  const { periodLabel, brand = "Amik" } = opts;
  const billing = data?.billing || {};
  const buyerBlock = buildBuyerBlock(billing);
  const cell =
    "padding:7px 8px;border:1px solid #000;vertical-align:top;line-height:1.45;";
  const num = "text-align:right;font-variant-numeric:tabular-nums;";

  // Tax lines inside the items table + the tax-summary rows both adapt to the
  // supply type: IGST inter-state, CGST + SGST intra-state.
  const taxItemRows = inv.intraState
    ? `
      <tr><td style="${cell}"></td><td style="${cell}text-align:right;font-weight:bold;">CGST</td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}${num}">${fmt(inv.cgst)}</td></tr>
      <tr><td style="${cell}"></td><td style="${cell}text-align:right;font-weight:bold;">SGST</td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}${num}">${fmt(inv.sgst)}</td></tr>`
    : `
      <tr><td style="${cell}"></td><td style="${cell}text-align:right;font-weight:bold;">IGST</td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}${num}">${fmt(inv.taxAmt)}</td></tr>`;

  const taxSummary = inv.intraState
    ? `
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <tr>
          <td rowspan="2" style="${cell}text-align:center;font-weight:bold;">HSN/SAC</td>
          <td rowspan="2" style="${cell}text-align:center;font-weight:bold;">Taxable Value</td>
          <td colspan="2" style="${cell}text-align:center;font-weight:bold;">CGST</td>
          <td colspan="2" style="${cell}text-align:center;font-weight:bold;">SGST</td>
          <td rowspan="2" style="${cell}text-align:center;font-weight:bold;">Total Tax Amount</td>
        </tr>
        <tr>
          <td style="${cell}text-align:center;">Rate</td><td style="${cell}text-align:center;">Amount</td>
          <td style="${cell}text-align:center;">Rate</td><td style="${cell}text-align:center;">Amount</td>
        </tr>
        <tr>
          <td style="${cell}">${SERVICE.hsn}</td>
          <td style="${cell}${num}">${fmt(inv.taxable)}</td>
          <td style="${cell}text-align:center;">${inv.gstRate / 2}%</td><td style="${cell}${num}">${fmt(inv.cgst)}</td>
          <td style="${cell}text-align:center;">${inv.gstRate / 2}%</td><td style="${cell}${num}">${fmt(inv.sgst)}</td>
          <td style="${cell}${num}">${fmt(inv.taxAmt)}</td>
        </tr>
        <tr>
          <td style="${cell}text-align:right;font-weight:bold;">Total</td>
          <td style="${cell}${num}font-weight:bold;">${fmt(inv.taxable)}</td>
          <td style="${cell}"></td><td style="${cell}${num}font-weight:bold;">${fmt(inv.cgst)}</td>
          <td style="${cell}"></td><td style="${cell}${num}font-weight:bold;">${fmt(inv.sgst)}</td>
          <td style="${cell}${num}font-weight:bold;">${fmt(inv.taxAmt)}</td>
        </tr>
      </table>`
    : `
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <tr>
          <td rowspan="2" style="${cell}text-align:center;font-weight:bold;">HSN/SAC</td>
          <td rowspan="2" style="${cell}text-align:center;font-weight:bold;">Taxable Value</td>
          <td colspan="2" style="${cell}text-align:center;font-weight:bold;">IGST</td>
          <td rowspan="2" style="${cell}text-align:center;font-weight:bold;">Total Tax Amount</td>
        </tr>
        <tr><td style="${cell}text-align:center;">Rate</td><td style="${cell}text-align:center;">Amount</td></tr>
        <tr>
          <td style="${cell}">${SERVICE.hsn}</td>
          <td style="${cell}${num}">${fmt(inv.taxable)}</td>
          <td style="${cell}text-align:center;">${inv.gstRate}%</td>
          <td style="${cell}${num}">${fmt(inv.taxAmt)}</td>
          <td style="${cell}${num}">${fmt(inv.taxAmt)}</td>
        </tr>
        <tr>
          <td style="${cell}text-align:right;font-weight:bold;">Total</td>
          <td style="${cell}${num}font-weight:bold;">${fmt(inv.taxable)}</td>
          <td style="${cell}"></td>
          <td style="${cell}${num}font-weight:bold;">${fmt(inv.taxAmt)}</td>
          <td style="${cell}${num}font-weight:bold;">${fmt(inv.taxAmt)}</td>
        </tr>
      </table>`;

  return `
  <div style="width:760px;background:#fff;color:#000;font-family:'Times New Roman',Times,serif;font-size:12px;line-height:1.45;padding:18px;box-sizing:border-box;">
    <div style="text-align:center;font-weight:bold;font-size:15px;margin-bottom:6px;">Tax Invoice</div>
    <table style="width:100%;border-collapse:collapse;">
      <tr>
        <td style="${cell}width:58%;">
          <div style="font-weight:bold;font-size:13px;">${esc(SELLER.name)}</div>
          ${SELLER.addressLines.map((l) => `<div>${esc(l)}</div>`).join("")}
          <div>GSTIN/UIN: ${esc(SELLER.gstin)}</div>
          <div>State Name : ${esc(SELLER.stateName)}, Code : ${esc(SELLER.stateCode)}</div>
        </td>
        <td style="padding:0;border:1px solid #000;vertical-align:top;">
          <table style="width:100%;border-collapse:collapse;">
            <tr><td style="${cell}width:50%;">Invoice No.<div style="font-weight:bold;">${esc(inv.invoiceNo)}</div></td>
                <td style="${cell}">Dated<div style="font-weight:bold;">${esc(inv.dated)}</div></td></tr>
            <tr><td style="${cell}">Period<div style="font-weight:bold;">${esc(periodLabel)}</div></td>
                <td style="${cell}">Mode/Terms of Payment</td></tr>
            <tr><td colspan="2" style="${cell}">Supply Type<div style="font-weight:bold;">${inv.intraState ? "Intra-State (CGST + SGST)" : "Inter-State (IGST)"}</div></td></tr>
          </table>
        </td>
      </tr>
      <tr>
        <td style="${cell}">
          <div style="color:#555;">Consignee (Ship to)</div>
          ${buyerBlock}
        </td>
        <td style="${cell}">
          <div style="color:#555;">Buyer (Bill to)</div>
          ${buyerBlock}
        </td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;margin-top:-1px;">
      <tr>
        <td style="${cell}text-align:center;font-weight:bold;width:32px;">Sl No.</td>
        <td style="${cell}font-weight:bold;">Particulars</td>
        <td style="${cell}text-align:center;font-weight:bold;">HSN/SAC</td>
        <td style="${cell}text-align:center;font-weight:bold;">Quantity</td>
        <td style="${cell}text-align:center;font-weight:bold;">Rate</td>
        <td style="${cell}text-align:center;font-weight:bold;">per</td>
        <td style="${cell}text-align:center;font-weight:bold;">Amount</td>
      </tr>
      <tr>
        <td style="${cell}text-align:center;">1</td>
        <td style="${cell}font-weight:bold;">${SERVICE.particulars}</td>
        <td style="${cell}text-align:center;">${SERVICE.hsn}</td>
        <td style="${cell}"></td>
        <td style="${cell}"></td>
        <td style="${cell}"></td>
        <td style="${cell}${num}font-weight:bold;">${fmt(inv.taxable)}</td>
      </tr>
      ${taxItemRows}
      <tr>
        <td style="${cell}"></td>
        <td style="${cell}text-align:right;font-weight:bold;">Round Off</td>
        <td style="${cell}"></td><td style="${cell}"></td><td style="${cell}"></td><td style="${cell}"></td>
        <td style="${cell}${num}">${fmt(inv.roundOff)}</td>
      </tr>
      <tr>
        <td colspan="6" style="${cell}text-align:right;font-weight:bold;">Total</td>
        <td style="${cell}${num}font-weight:bold;font-size:13px;">&#8377; ${fmt(inv.total)}</td>
      </tr>
    </table>

    <div style="border:1px solid #000;border-top:none;padding:6px 8px;">
      <span style="color:#555;">Amount Chargeable (in words)</span>
      <div style="font-weight:bold;">${esc(amountInWords(inv.total))}</div>
    </div>

    <div style="margin-top:8px;">${taxSummary}</div>

    <div style="border:1px solid #000;border-top:none;padding:6px 8px;">
      <span style="color:#555;">Tax Amount (in words) :</span>
      <span style="font-weight:bold;">${esc(amountInWords(inv.taxAmt))}</span>
    </div>

    <table style="width:100%;border-collapse:collapse;margin-top:8px;">
      <tr>
        <td style="${cell}width:60%;font-size:11px;color:#555;">
          ${esc(brand)} CMS/Service fee for the period ${esc(periodLabel)}.
          Invoice number is system-generated for reference.
        </td>
        <td style="${cell}text-align:right;">
          <div style="font-weight:bold;">for ${esc(SELLER.name)}</div>
          <div style="height:42px;"></div>
          <div>Authorised Signatory</div>
        </td>
      </tr>
    </table>
    <div style="text-align:center;font-size:11px;margin-top:6px;">This is a Computer Generated Invoice</div>
  </div>`;
};

/**
 * Build the Amik service-fee tax invoice from a payout report `data` object and
 * download it as an A4 PDF. Requires data.billing (added by the payout-report API)
 * and data.payout.amikFee. opts: { periodLabel, month, year, teamId, brand,
 * invoiceNoOverride }. When a ledger record exists its (possibly edited) number is
 * passed as invoiceNoOverride so the PDF matches the saved invoice.
 */
export async function downloadServiceFeeInvoice(data, opts = {}) {
  if (!data?.payout) throw new Error("No payout data to invoice.");
  if (!(Number(data?.payout?.amikFee) > 0)) {
    throw new Error(
      "No Amik service fee for this period — nothing to invoice."
    );
  }
  const inv = computeInvoice(data, opts);
  if (clean(opts.invoiceNoOverride)) inv.invoiceNo = clean(opts.invoiceNoOverride);
  const html = buildInvoiceHtml(data, inv, opts);

  const wrap = document.createElement("div");
  wrap.style.cssText = "position:fixed;left:-10000px;top:0;background:#fff;";
  wrap.innerHTML = html;
  document.body.appendChild(wrap);
  try {
    const html2canvas = (await import("html2canvas")).default;
    const node = wrap.firstElementChild;
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff" });
    const pdf = new jsPDF("p", "mm", "a4");
    const margin = 8;
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    const w = pw - margin * 2;
    const fullH = (canvas.height / canvas.width) * w;

    if (fullH <= ph - margin * 2) {
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, margin, w, fullH);
    } else {
      // Taller than one page: slice the canvas into page-height strips.
      const pagePx = ((ph - margin * 2) * canvas.width) / w;
      let sy = 0;
      let first = true;
      while (sy < canvas.height) {
        const sliceH = Math.min(pagePx, canvas.height - sy);
        const c = document.createElement("canvas");
        c.width = canvas.width;
        c.height = sliceH;
        c.getContext("2d").drawImage(
          canvas, 0, sy, canvas.width, sliceH, 0, 0, canvas.width, sliceH
        );
        if (!first) pdf.addPage();
        pdf.addImage(
          c.toDataURL("image/png"), "PNG", margin, margin, w,
          (sliceH / canvas.width) * w
        );
        first = false;
        sy += sliceH;
      }
    }
    pdf.save(`Amik_Service_Fee_Invoice_${inv.invoiceNo.replace(/\//g, "-")}.pdf`);
  } finally {
    document.body.removeChild(wrap);
  }
}
